import { obtenerTokenDePlataforma } from '../autenticacionPlataforma'
import { ErrorDeApi, fetchDeApi, tokenDeSesion, type ResultadoDeInvitacion, type ResumenDeCatalogo } from './api'

const baseUrl = import.meta.env.VITE_API_URL ?? ''

/**
 * El 403 que la política de plataforma responde SIN cuerpo: el token es válido pero no trae el rol
 * de aplicación. Se veía como «Error 403», que no dice qué falta ni a quién pedírselo. Un 403 CON
 * cuerpo (falta de consentimiento del despacho, §6.4) trae su propio mensaje y no pasa por aquí.
 */
export const MENSAJE_SIN_ROL_DE_PLATAFORMA =
  'Tu cuenta no tiene el rol Plataforma.Administrador. Pídele a quien administra el directorio de Entra ID de ' +
  'ContaQuick que te lo asigne en la aplicación «ContaQuick Plataforma API»; después cierra sesión y vuelve a entrar.'

/**
 * Cliente HTTP de la consola interna de plataforma (RF-107). A propósito es un módulo
 * aparte de `./api.ts`: usa el token de `autenticacionPlataforma` (esquema "Plataforma" en
 * el API), nunca el de la sesión de despacho, y solo habla con `/api/plataforma/*` y el
 * historial de consentimientos que el propio despacho expone.
 */
async function peticion<T>(ruta: string, opciones: RequestInit = {}): Promise<T> {
  const token = await tokenDeSesion(obtenerTokenDePlataforma)

  const respuesta = await fetchDeApi(`${baseUrl}${ruta}`, {
    ...opciones,
    headers: {
      // Con FormData el Content-Type lo pone el navegador: lleva el boundary del multipart, y
      // fijarlo a mano (o dejar el de JSON) hace que el API no encuentre el archivo.
      ...(opciones.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
      Authorization: `Bearer ${token}`,
      ...opciones.headers,
    },
  })

  if (!respuesta.ok) {
    let mensaje = `Error ${respuesta.status}`
    let codigo: string | undefined

    try {
      const cuerpo = await respuesta.json()
      mensaje = cuerpo.error ?? mensaje
      codigo = cuerpo.codigo
    } catch {
      /* la respuesta no traía JSON */
      if (respuesta.status === 403) mensaje = MENSAJE_SIN_ROL_DE_PLATAFORMA
    }

    throw new ErrorDeApi(respuesta.status, mensaje, codigo)
  }

  return respuesta.status === 204 ? (undefined as T) : ((await respuesta.json()) as T)
}

export type PlanSuscripcion = 'Gratuito' | 'Pro' | 'Despacho'
export type EstadoDeTenant = 'Activo' | 'Suspendido'

export interface DespachoDePlataforma {
  id: string
  nombreComercial: string
  plan: PlanSuscripcion
  estado: EstadoDeTenant
  clientesActivos: number
  usuariosActivos: number
  creadoEnUtc: string
  /** Topes del plan contratado (X4). Los tres son finitos: no hay plan ilimitado. */
  maxClientes: number
  maxUsuarios: number
  maxCfdiPorMes: number
  /** CFDI procesados en el mes corriente (X6). */
  cfdiDelMes: number
}

/**
 * Los cuatro indicadores de la portada de la consola (X1). Son de uso y ciclo de vida, nunca
 * contenido contable: ninguno necesita el consentimiento del despacho (RF-107 §6.4).
 */
export interface MetricasDePlataforma {
  despachosActivos: number
  despachosSuspendidos: number
  contribuyentes: number
  cfdiDelMes: number
  solicitudesSatEnCola: number
  anio: number
  mes: number
}

/** Mismo contrato que el tablero del despacho (RF-901): reutilizado tal cual por el resumen contable de soporte. */
export interface ResumenDeClienteDePlataforma {
  clienteId: string
  rfc: string
  razonSocial: string
  periodosAbiertos: number
  cfdiSinContabilizar: number
  enviosPendientes: number
  enviosRechazados: number
  semaforo: 'AlCorriente' | 'PorVencer' | 'Vencido'
}

export interface AltaDeDespacho {
  nombreComercial: string
  correoContacto: string
  nombreAdministrador: string
  rfcAdministrador?: string
  telefonoAdministrador?: string
}

export interface DespachoDadoDeAlta extends ResultadoDeInvitacion {
  despachoId: string
  usuarioAdministradorId: string
}

/** Mismo enum que `EstadoDeCargaDeCatalogo` del dominio. */
export type EstadoDeCargaDeCatalogo = 'EnProceso' | 'Terminada' | 'Error' | 'SinCambios'

/** Lo que devuelve una carga (RF-1003). `errores` trae solo los primeros renglones malos. */
export interface ResumenDeCargaDeCatalogo {
  catalogo: string
  estado: EstadoDeCargaDeCatalogo
  filas: number
  nuevas: number
  actualizadas: number
  vencidas: number
  conError: number
  errores: string[]
  huellaSha256: string | null
}

export interface CargaDeCatalogoSolicitada {
  archivo: File
  version: string
  origen: string
  /** AAAA-MM-DD. Se omite si no se sabe: mejor sin dato que una fecha inventada. */
  publicadoPorSat?: string
}

export const apiPlataforma = {
  listarDespachos: () => peticion<DespachoDePlataforma[]>('/api/plataforma/despachos'),

  /** X1: indicadores de uso de toda la plataforma. Sin año y mes, el periodo es el corriente. */
  metricas: () => peticion<MetricasDePlataforma>('/api/plataforma/metricas'),

  /** RF-101, RF-107: único punto de alta de despachos — ya no hay registro público. */
  altaDeDespacho: (alta: AltaDeDespacho) =>
    peticion<DespachoDadoDeAlta>('/api/plataforma/despachos', { method: 'POST', body: JSON.stringify(alta) }),

  cambiarPlan: (despachoId: string, plan: PlanSuscripcion) =>
    peticion<void>(`/api/plataforma/despachos/${despachoId}/plan?plan=${plan}`, { method: 'POST' }),

  suspender: (despachoId: string) =>
    peticion<void>(`/api/plataforma/despachos/${despachoId}/suspender`, { method: 'POST' }),

  reactivar: (despachoId: string) =>
    peticion<void>(`/api/plataforma/despachos/${despachoId}/reactivar`, { method: 'POST' }),

  /** 403 con codigo SIN_CONSENTIMIENTO_DE_SOPORTE si el despacho no otorgó consentimiento vigente (§6.4). */
  resumenContable: (despachoId: string) =>
    peticion<ResumenDeClienteDePlataforma[]>(`/api/plataforma/despachos/${despachoId}/resumen-contable`),

  /** RF-1003: mismo resumen que ve el contador en /catalogos, servido bajo el esquema de plataforma. */
  estadoDeCatalogos: () => peticion<ResumenDeCatalogo[]>('/api/plataforma/catalogos/'),

  /** RF-1003: carga un catálogo nacional desde su CSV normalizado. Solo la plataforma, nunca un despacho. */
  cargarCatalogo: (catalogo: string, carga: CargaDeCatalogoSolicitada) => {
    const formulario = new FormData()
    formulario.append('archivo', carga.archivo)
    formulario.append('version', carga.version)
    formulario.append('origen', carga.origen)
    if (carga.publicadoPorSat) formulario.append('publicadoPorSat', carga.publicadoPorSat)
    return peticion<ResumenDeCargaDeCatalogo>(
      `/api/plataforma/catalogos/${encodeURIComponent(catalogo)}/carga`,
      { method: 'POST', body: formulario },
    )
  },
}
